import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";

import { useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { green, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CircularProgress, Divider, Switch } from "@mui/material";
import { PimDashboardState } from "context/PimDashboardContext";
import { attrGroupLinkedTables } from "components/PimDashboard/data";
import {
	converAttrToDatagridCol,
	createdByColumns,
	imageTableColumn,
	styleImageColumn,
} from "data/globalData";
import { FileDownloadRounded, FilterAltRounded, PlaylistAddRounded, UploadRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	BUY_QTY_SHEET_API,
	buyQtyPublishColumns,
	buyQtySheetAttrStorageStr,
	buyQtySheetFilterValStr,
	buyQtyStageColumn,
	buyQtyStatusColumn,
	dedupeDataGridColumns,
	filterBuyQtyAttribute,
} from "./data";
import _ from "lodash";
import EditDetailsModal from "./EditDetailsModal/EditDetailsModal";
import MrpUploadModal from "./UploadModal/UploadModal";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

export function setColorsCustomFun(prev, newRows) {
	const map = {};

	const result = [...prev, ...newRows]?.reduce((acc, row) => {
		if (map[row.option_id]) return acc;
		else {
			acc.push(row);
			map[row.option_id] = true;
		}
		return acc;
	}, []);
	return result;
}

const BuyQtySheet = () => {
	const gridRef = useRef();
	const { allAttributes, allAttributesObj } = PimDashboardState();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [colors, setColors] = useState([]);
	const [displayImg, setDisplayImg] = useState(true);
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const [selectedColor, setSelectedColor] = useState({});
	const [uploadModal, setUploadModal] = useState(false);

	const defColumns = [
		{
			field: "option_id",
			headerName: "Option ID",
			width: 140,
			...colStyle,
		},
	];

	const [styleTablColumns, setStyleTableColumns] = useState(defColumns);

	const [attrColumns, setAttrColumns] = useState(() => {
		if (localStorage.getItem(buyQtySheetAttrStorageStr)) {
			return JSON.parse(localStorage.getItem(buyQtySheetAttrStorageStr))
				?.map((row) => ({ ...allAttributesObj?.[row] }))
				?.filter(filterBuyQtyAttribute)
				?.map((row) => converAttrToDatagridCol(row));
		}

		return allAttributes
			?.filter(
				(row) =>
					filterBuyQtyAttribute(row) &&
					(row?.linked_table === attrGroupLinkedTables.style.key ||
						row?.linked_table === attrGroupLinkedTables.color.key),
			)
			?.map((row) => converAttrToDatagridCol(row));
	});

	const [filterColumns, setFilterColumns] = useState(
		// appling filter fron local storage data
		() => {
			let final = [];
			if (localStorage.getItem(buyQtySheetFilterValStr)) {
				const localStorageObj =
					typeof JSON.parse(localStorage.getItem(buyQtySheetFilterValStr)) === "object"
						? JSON.parse(localStorage.getItem(buyQtySheetFilterValStr))
						: {};
				[
					...defColumns,
					...allAttributes,
					...createdByColumns,
					...Object.keys(buyQtyPublishColumns).map((row) => buyQtyPublishColumns[row]),
				]?.forEach((row) => {
					if (localStorageObj?.[row?.key]) final.push(row);
				});
				return final;
			}
		},
	);
	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(buyQtySheetFilterValStr)) {
			return JSON.parse(localStorage.getItem(buyQtySheetFilterValStr));
		} else {
			return {};
		}
	});
	const [offset, setOffset] = useState(0);
	const [recordsLimit] = useState(20);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);

	const handleCloseFilterModal = () => {
		setFilterModal(false);
	};

	const handleCloseAddColumnModal = () => {
		setAddColumnModal(false);
	};

	const handleFilterClick = () => {
		setFilterModal(true);
	};

	const handleAddColumnClick = () => {
		setAddColumnModal(true);
	};

	const reloadColors = async () => {
		setLoadingOpen(true);
		setOffset(0);
		setHasMore(true);
		try {
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});
			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/get_colors/0`, {
				filter: filterColumnVal,
				extraColumns,
				limit: recordsLimit,
			});
			setColors(data.result.colors);
			setTableStatusCount(data.result.count[0]);
			setOffset(recordsLimit);
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	const fetchColors = async (offset, limit) => {
		try {
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});

			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/get_colors/${offset}`, {
				filter: filterColumnVal,
				extraColumns,
				limit,
			});
			setColors((prev) => setColorsCustomFun(prev, data.result.colors));
			setTableStatusCount(data.result.count[0]);
			setOffset((prev) => prev + recordsLimit);
			return data.result || [];
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			return [];
		}
	};

	const loadMoreData = useCallback(
		_.throttle(async () => {
			if (loading || !hasMore) return;
			if (colors?.length > 100) return;
			setLoading(true);

			const newRows = await fetchColors(offset, recordsLimit);

			if (newRows.colors?.length < recordsLimit) setHasMore(false);
			setLoading(false);
		}, 2000),
		[loading, hasMore, offset],
	);

	useEffect(() => {
		const gridElement = gridRef.current;
		if (!gridElement) return;

		const scrollContainer = gridElement.querySelector(".MuiDataGrid-virtualScroller");

		if (!scrollContainer) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
			if (scrollHeight - scrollTop <= clientHeight + 100) {
				loadMoreData();
			}
		};

		scrollContainer.addEventListener("scroll", handleScroll);

		return () => {
			scrollContainer.removeEventListener("scroll", handleScroll);
		};
	}, [loadMoreData]);
	useEffect(() => {
		const fetchInitialColors = async () => {
			setLoadingOpen(true);
			setStyleTableColumns(
				dedupeDataGridColumns([
					buyQtyStatusColumn(),
					buyQtyStageColumn(),
					imageTableColumn(styleImageColumn),
					...attrColumns,
					...defColumns,
					...Object.keys(buyQtyPublishColumns)
						.filter((key) => key !== "buy_qty_status")
						.map((row) => converAttrToDatagridCol(buyQtyPublishColumns[row])),
				]),
			);
			try {
				const result = await fetchColors(0, recordsLimit);
				setColors(result.colors);
				if (result?.count?.[0]) setTableStatusCount(result?.count?.[0]);
				setOffset((prev) => prev + recordsLimit);
				setHasMore(result?.colors?.length === recordsLimit);
				setLoadingOpen(false);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
				setLoadingOpen(false);
			}
		};

		fetchInitialColors();
	}, [filterColumns, attrColumns]);

	const navigate = useNavigate();

	const handleImageSwitchChange = (checked) => {
		setDisplayImg(checked);
	};

	const handleColorSelect = async (data) => {
		setSelectedColor(data);
		setEditModal(true);
	};

	const selectedColorIndexRaw = colors?.findIndex((row) => row.option_id === selectedColor?.option_id) ?? -1;
	const selectedColorIndex = selectedColorIndexRaw >= 0 ? selectedColorIndexRaw : 0;

	const handleNextColor = () => {
		if (selectedColorIndex >= 0 && selectedColorIndex < colors.length - 1) {
			setSelectedColor(colors[selectedColorIndex + 1]);
		}
	};

	const handlePrevColor = () => {
		if (selectedColorIndex > 0) {
			setSelectedColor(colors[selectedColorIndex - 1]);
		}
	};
	const handleDownloadClick = async () => {
		setLoadingOpen(true);
		try {
			await axios({
				url: `${BUY_QTY_SHEET_API}/download_buy_qty_sheet`,
				method: "POST",
				responseType: "blob",
				data: { filter: filterColumnVal },
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `buyQtySheet.xlsx`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	const handleUploadSave = async (rows, { publish = false } = {}) => {
		const validRows = rows.filter((row) => row.__isValid !== false);

		if (!validRows.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Upload file must contain valid option_id, channel, warehouse, size, and total_qty values",
				severity: "warning",
			});
			return false;
		}

		setLoadingOpen(true);
		try {
			const response = await axios.post(`${BUY_QTY_SHEET_API}/bulk_upload_buy_qty`, {
				publish,
				rows: validRows,
			});
			const responseData = response?.data || {};

			if (responseData.status === "error") {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: responseData.message || "Upload failed",
					severity: "error",
				});
				return responseData;
			}

			const processed = responseData?.result?.processed ?? validRows.length;
			const successful = responseData?.result?.successful ?? validRows.length;
			const failed = Math.max(processed - successful, 0);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message:
					failed > 0
						? `${successful} color(s) uploaded successfully, ${failed} failed`
						: `${successful} color(s) uploaded successfully`,
				severity: failed > 0 ? "warning" : "success",
			});
			setUploadModal(false);
			reloadColors();
			return responseData;
		} catch (error) {
			const errorData = error?.response?.data;
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: errorData?.message || parseErrorMessage(error),
				severity: "error",
			});
			return errorData || false;
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleColorUpdate = () => {
		reloadColors();
	};
	return (
		<Stack width="100%" pt={1}>
			<Box display="flex" justifyContent="space-around">
				{/* left section */}
				<Stack spacing={1} width="28%">
					<Box display="flex" columnGap={1} pt={1} flexWrap="wrap" alignItems="center" rowGap={"5px"}>
						<Button
							variant="contained"
							size="small"
							startIcon={<FilterAltRounded />}
							onClick={handleFilterClick}
							sx={{ textTransform: "capitalize" }}>
							Filter
						</Button>
						:
						{!filterColumns?.length && (
							<Typography variant="body1" fontSize={16} color={grey[700]}>
								None
							</Typography>
						)}
						{filterColumns?.map((row) => {
							return (
								<Chip
									label={`${row.name} : ${
										Array.isArray(filterColumnVal[row?.key])
											? filterColumnVal[row?.key]?.join(", ")
											: filterColumnVal?.[row?.key]
									}`}
									size="small"
								/>
							);
						})}
					</Box>
					<Box>
						<Button
							variant="contained"
							sx={{ textTransform: "capitalize", mr: 1 }}
							size="small"
							onClick={handleAddColumnClick}
							startIcon={<PlaylistAddRounded />}>
							Manage Columns
						</Button>
						<Button
							variant="contained"
							size="small"
							sx={{ textTransform: "capitalize" }}
							onClick={handleDownloadClick}
							startIcon={<FileDownloadRounded />}>
							Download
						</Button>
					</Box>
				</Stack>
				{/* right section */}
				<Box width="68%"></Box>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" pt={1}>
				<Box display="flex" alignItems="center">
					<Typography variant="h6">Images</Typography>
					<Switch checked={displayImg} onChange={(e) => handleImageSwitchChange(e.target.checked)} sx={{ mt: 1 }} />
				</Box>
				<Box display="flex" alignItems="center" justifyContent="space-between" width="100%" columnGap={2}>
					<Box display="flex" alignItems="center" columnGap={1} flexWrap="wrap">
						<Typography variant="body1">
							Total: <span style={{ color: grey[700] }}>{tableStatusCount.total}</span>
						</Typography>
						<Divider orientation="vertical" flexItem />
						<Typography variant="body1">
							Published: <span style={{ color: green[600] }}>{tableStatusCount.published}</span>
						</Typography>
						<Divider orientation="vertical" flexItem />
						<Typography variant="body1" fontWeight={600}>
							Pending: <span style={{ color: "red" }}>{tableStatusCount.pending}</span>
						</Typography>
					</Box>
					<Button
						variant="contained"
						size="small"
						sx={{ textTransform: "capitalize" }}
						onClick={() => setUploadModal(true)}
						startIcon={<UploadRounded />}>
						Upload Data
					</Button>
				</Box>
			</Box>

			<Box height="60vh" ref={gridRef}>
				<DataGrid
					rows={colors ? colors : []}
					key={displayImg}
					columns={styleTablColumns ?? []}
					sx={{
						// minWidth: "100%"
						border: "none",
						"& .MuiDataGrid-row": {
							// borderBottom: "1px solid lightgray",
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							background: grey[300],
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
						"& .MuiDataGrid-footer": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
					}}
					columnHeaderHeight={35}
					rowHeight={displayImg ? 70 : 40}
					getRowId={(row) => row?.option_id}
					onRowClick={(params, e) => handleColorSelect(params.row)}
					initialState={{
						columns: {
							columnVisibilityModel: {
								[styleImageColumn.key]: displayImg,
							},
						},
					}}
					disableRowSelectionOnClick
					hideFooter={true}
					pagination={false}
					// disableColumnFilter
					disableColumnSelector
					disableAutosize
				/>
				{/* <Box sx={{ minHeight: "20px" }}> */}
				{loading && (
					<Box display="flex" sx={{ background: grey[400], borderRadius: 2, p: 1 }} alignItems="center">
						<Typography variant="body2" color={grey[900]}>
							Loading...
						</Typography>
						<CircularProgress size="18px" sx={{ transform: "scale(0.4)" }} />
					</Box>
				)}
			</Box>

			<FilterModal
				modal={filterModal}
				handleCloseModal={handleCloseFilterModal}
				filterColumns={filterColumns}
				setFilterColumns={setFilterColumns}
				filterColumnVal={filterColumnVal}
				setFilterColumnVal={setFilterColumnVal}
				localStorageStr={buyQtySheetFilterValStr}
			/>
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={handleCloseAddColumnModal}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={buyQtySheetAttrStorageStr}
			/>
			{editModal && (
				<EditDetailsModal
					modal={editModal}
					setModal={setEditModal}
					selectedColor={selectedColor}
					setSelectedColor={setSelectedColor}
					saveCallback={handleColorUpdate}
					currentIndex={selectedColorIndex}
					totalCount={colors?.length ?? 0}
					onPrev={handlePrevColor}
					onNext={handleNextColor}
				/>
			)}
			<MrpUploadModal modal={uploadModal} setModal={setUploadModal} callbackFun={handleUploadSave} />
		</Stack>
	);
};

export default BuyQtySheet;
